import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { ClockWidget } from './widgets/ClockWidget.tsx'
import CalendarTodayWidget from './widgets/CalendarTodayWidget.tsx'
import CallsWidget from './widgets/CallsWidget.tsx'
import CallbacksWidget from './widgets/CallbacksWidget.tsx'
import TimeStatusWidget from './widgets/TimeStatusWidget.tsx'
import ExpiringContractsWidget from './widgets/ExpiringContractsWidget.tsx'
import { WidgetGrid, useWidgetLayout, type WidgetConfig } from '@/components/widgets/WidgetGrid.tsx'
import { WidgetWrapper } from '@/components/widgets/WidgetWrapper.tsx'
import {
  AlertTriangle, Ticket, UserX, Users, ChevronDown, ChevronRight,
  Calendar, Phone, PhoneForwarded, Clock, Mail, Bell, GripVertical, X, FileText,
} from 'lucide-react'
import { useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
} from '@/components/ui'

// ─── Types ───────────────────────────────────────────────────────────────────

interface TanssTicket {
  id: number
  title?: string
  nr?: string
  content?: string
  companyName?: string
  company?: { name?: string }
  status?: string
  statusId?: number
  assignedToEmployeeName?: string
  assignee?: { name?: string }
  queue?: string
  department?: { name?: string }
  category?: string
  categoryName?: string
  tags?: { name: string; color?: string }[]
  createdDate?: string
  dueDate?: string
  orderDate?: string
  attention?: string
  [key: string]: unknown
}

const AVAILABLE_WIDGETS = [
  { type: 'clock', label: 'Stempeluhr', defaultW: 12, defaultH: 2 },
  { type: 'my-tickets', label: 'Meine offenen Tickets', defaultW: 4, defaultH: 8 },
  { type: 'my-tickets-mail', label: 'Meine Tickets mit neuen Emails', defaultW: 4, defaultH: 8 },
  { type: 'my-tickets-changes', label: 'Meine Tickets mit Änderungen', defaultW: 4, defaultH: 8 },
  { type: 'unassigned-tickets', label: 'Nicht zugewiesene Tickets', defaultW: 6, defaultH: 8 },
  { type: 'all-tickets', label: 'Tickets aller Mitarbeiter', defaultW: 6, defaultH: 8 },
  { type: 'overdue-alert', label: 'Überfällige Tickets', defaultW: 12, defaultH: 2 },
  { type: 'calendar-today', label: 'Heutige Termine', defaultW: 4, defaultH: 4 },
  { type: 'calls-today', label: 'Anrufe heute', defaultW: 4, defaultH: 3 },
  { type: 'callbacks-open', label: 'Offene Rückrufe', defaultW: 4, defaultH: 4 },
  { type: 'time-status', label: 'Arbeitszeitstatus', defaultW: 4, defaultH: 3 },
  { type: 'expiring-contracts', label: 'Ablaufende Verträge', defaultW: 6, defaultH: 6 },
]

// ─── Main Page ───────────────────────────────────────────────────────────────

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => apiClient.get('/dashboard').then((r) => r.data),
    refetchInterval: 2 * 60 * 1000,
  })

  const { layout, save } = useWidgetLayout()

  const handleLayoutChange = useCallback(
    (newLayout: WidgetConfig[]) => {
      save(newLayout)
    },
    [save],
  )

  const today = new Date()
  const dateStr = today.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  const greeting = data?.greeting || getGreeting()
  const standTime = today.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })

  const myTickets: TanssTicket[] = data?.myTickets ?? []
  const myTicketsWithMail = useMemo(
    () => myTickets.filter((t) => t.attention === 'MAIL'),
    [myTickets],
  )
  const myTicketsWithChanges = useMemo(
    () => myTickets.filter((t) => t.attention === 'YES'),
    [myTickets],
  )

  const renderWidget = useCallback(
    (config: WidgetConfig, isEditing: boolean, onRemove: () => void) => {
      switch (config.type) {
        case 'clock':
          return (
            <BareWidget isEditing={isEditing} onRemove={onRemove}>
              <ClockWidget
                greeting={`${greeting}, ${user?.name?.split(' ')[0]}!`}
                dateStr={dateStr}
                standTime={standTime}
              />
            </BareWidget>
          )

        case 'my-tickets':
          return (
            <WidgetWrapper
              title="Meine offenen Tickets"
              icon={<Ticket className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
              onRefresh={() => qc.invalidateQueries({ queryKey: ['dashboard'] })}
            >
              <TicketListContent
                tickets={myTickets}
                loading={isLoading}
                emptyMessage="Keine offenen Tickets"
                countVariant="brand"
              />
            </WidgetWrapper>
          )

        case 'my-tickets-mail':
          return (
            <WidgetWrapper
              title="Meine Tickets mit neuen Emails"
              icon={<Mail className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
              onRefresh={() => qc.invalidateQueries({ queryKey: ['dashboard'] })}
            >
              <TicketListContent
                tickets={myTicketsWithMail}
                loading={isLoading}
                emptyMessage="Keine neuen Emails"
                countVariant="brand"
              />
            </WidgetWrapper>
          )

        case 'my-tickets-changes':
          return (
            <WidgetWrapper
              title="Meine Tickets mit Änderungen"
              icon={<Bell className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
              onRefresh={() => qc.invalidateQueries({ queryKey: ['dashboard'] })}
            >
              <TicketListContent
                tickets={myTicketsWithChanges}
                loading={isLoading}
                emptyMessage="Keine Änderungen"
                countVariant="warning"
              />
            </WidgetWrapper>
          )

        case 'unassigned-tickets':
          return (
            <WidgetWrapper
              title="Nicht zugewiesene Tickets"
              icon={<UserX className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
              onRefresh={() => qc.invalidateQueries({ queryKey: ['dashboard'] })}
            >
              <TicketListContent
                tickets={data?.unassignedTickets ?? []}
                loading={isLoading}
                emptyMessage="Alle Tickets sind zugewiesen"
                countVariant="warning"
              />
            </WidgetWrapper>
          )

        case 'all-tickets':
          return (
            <WidgetWrapper
              title="Tickets aller Mitarbeiter"
              icon={<Users className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
              onRefresh={() => qc.invalidateQueries({ queryKey: ['dashboard'] })}
            >
              <TicketListContent
                tickets={data?.allTickets ?? []}
                loading={isLoading}
                emptyMessage="Keine offenen Tickets"
                countVariant="neutral"
                grouped
              />
            </WidgetWrapper>
          )

        case 'overdue-alert':
          if (!data?.overdueCount && !isEditing) return <div />
          return (
            <BareWidget isEditing={isEditing} onRemove={onRemove}>
              <div className="w-full flex items-center gap-2 rounded-md bg-red-600 text-white px-4 py-2.5">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-body font-medium">
                  {data?.overdueCount ?? 0} überfällige{(data?.overdueCount ?? 0) === 1 ? 's' : ''} Ticket{(data?.overdueCount ?? 0) === 1 ? '' : 's'}
                </span>
              </div>
            </BareWidget>
          )

        case 'calendar-today':
          return (
            <WidgetWrapper
              title="Heutige Termine"
              icon={<Calendar className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
            >
              <div className="p-3">
                <CalendarTodayWidget />
              </div>
            </WidgetWrapper>
          )

        case 'calls-today':
          return (
            <WidgetWrapper
              title="Anrufe heute"
              icon={<Phone className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
            >
              <div className="p-3">
                <CallsWidget />
              </div>
            </WidgetWrapper>
          )

        case 'callbacks-open':
          return (
            <WidgetWrapper
              title="Offene Rückrufe"
              icon={<PhoneForwarded className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
            >
              <div className="p-3">
                <CallbacksWidget />
              </div>
            </WidgetWrapper>
          )

        case 'time-status':
          return (
            <WidgetWrapper
              title="Arbeitszeitstatus"
              icon={<Clock className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
            >
              <div className="p-3">
                <TimeStatusWidget />
              </div>
            </WidgetWrapper>
          )

        case 'expiring-contracts':
          return (
            <WidgetWrapper
              title="Ablaufende Verträge"
              icon={<FileText className="w-4 h-4" />}
              isEditing={isEditing}
              onRemove={onRemove}
            >
              <ExpiringContractsWidget onRefresh={() => qc.invalidateQueries({ queryKey: ['contracts-expiry-report'] })} />
            </WidgetWrapper>
          )

        case 'today-overview':
          // Tagesuebersicht wurde entfernt – Legacy-Layout-Eintrag bleibt loeschbar.
          return (
            <BareWidget isEditing={isEditing} onRemove={onRemove}>
              <div className="w-full h-full rounded-md border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-caption text-gray-400 px-3">
                Tagesübersicht (entfernt) – im Bearbeiten-Modus über X löschen
              </div>
            </BareWidget>
          )

        default:
          return (
            <BareWidget isEditing={isEditing} onRemove={onRemove}>
              <div className="w-full h-full rounded-md bg-gray-100 flex items-center justify-center text-small text-gray-400">
                Unbekanntes Widget
              </div>
            </BareWidget>
          )
      }
    },
    [data, myTickets, myTicketsWithMail, myTicketsWithChanges, isLoading, qc, dateStr, greeting, standTime, user?.name],
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${greeting}, ${user?.name?.split(' ')[0]}!`}
        description={dateStr}
        actions={<span className="text-caption text-gray-400">Stand: {standTime}</span>}
      />

      {isError && (
        <ErrorState
          title="Dashboard-Daten konnten nicht geladen werden"
          description="Die Verbindung zum Server ist fehlgeschlagen."
          details={error ? JSON.stringify(error, Object.getOwnPropertyNames(error as object), 2) : undefined}
          onRetry={() => window.location.reload()}
        />
      )}

      <WidgetGrid
        widgets={layout}
        onLayoutChange={handleLayoutChange}
        renderWidget={renderWidget}
        availableWidgets={AVAILABLE_WIDGETS}
      />

      {document.getElementById('footer-portal') && createPortal(
        <>
          {data && !data.tanssConnected && (
            <Badge variant="warning" size="sm" className="gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {data.tanssError}
            </Badge>
          )}
          {isError && (
            <Badge variant="danger" size="sm" className="gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              Fehler beim Laden der Dashboard-Daten.
            </Badge>
          )}
        </>,
        document.getElementById('footer-portal')!,
      )}
    </div>
  )
}

// ─── Ticket List Content (inner content for widget wrapper) ─────────────────

function TicketListContent({
  tickets,
  loading,
  emptyMessage,
  countVariant,
  grouped,
}: {
  tickets: TanssTicket[]
  loading: boolean
  emptyMessage: string
  countVariant: 'brand' | 'warning' | 'neutral'
  grouped?: boolean
}) {
  const [showAll, setShowAll] = useState(false)

  const groups = grouped ? groupTickets(tickets) : null
  const visibleTickets = showAll ? tickets : tickets.slice(0, 8)

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-1.5">
        <Badge variant={countVariant} size="sm">
          {tickets.length} Tickets
        </Badge>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <LoadingState size="sm" label="" />
        ) : tickets.length === 0 ? (
          <EmptyState size="sm" title={emptyMessage} />
        ) : grouped && groups ? (
          <div className="divide-y divide-gray-100">
            {Object.entries(groups).map(([groupName, groupTickets]) => (
              <GroupedSection key={groupName} name={groupName} tickets={groupTickets} />
            ))}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {visibleTickets.map((ticket) => (
              <TicketRow key={ticket.id} ticket={ticket} />
            ))}
          </div>
        )}
      </div>

      {!grouped && tickets.length > 8 && !showAll && (
        <div className="border-t border-gray-100">
          <Button
            variant="link"
            size="xs"
            onClick={() => setShowAll(true)}
            className="px-4 py-2 w-full justify-start"
          >
            {tickets.length - 8} weitere anzeigen
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── Grouped Section ─────────────────────────────────────────────────────────

function GroupedSection({ name, tickets }: { name: string; tickets: TanssTicket[] }) {
  const [open, setOpen] = useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 text-left transition"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
          <span className="text-caption font-semibold text-gray-600 uppercase">{name}</span>
        </div>
        <span className="text-caption text-gray-400">({tickets.length})</span>
      </button>
      {open && (
        <div className="divide-y divide-gray-50">
          {tickets.map((ticket) => (
            <TicketRow key={ticket.id} ticket={ticket} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Ticket Row ──────────────────────────────────────────────────────────────

function TicketRow({ ticket }: { ticket: TanssTicket }) {
  const navigate = useNavigate()
  const companyName = ticket.companyName || ticket.company?.name || ''
  const ticketTitle = ticket.title || ticket.content || ''
  const ticketNr = ticket.nr || `#${ticket.id}`
  const date = ticket.orderDate || ticket.createdDate || ticket.dueDate
  const assigneeName = ticket.assignedToEmployeeName || ticket.assignee?.name
  const tags = Array.isArray(ticket.tags) ? ticket.tags : []

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/tickets/${ticket.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate(`/tickets/${ticket.id}`)
        }
      }}
      className="px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none focus:bg-gray-50"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {companyName && (
            <p className="text-body font-semibold text-brand-700 truncate">{companyName}</p>
          )}
          <p className="text-body text-gray-700 truncate">{ticketTitle}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {tags.map((tag, i) => (
              <Badge
                key={i}
                variant="neutral"
                size="sm"
                style={tag.color ? { backgroundColor: tag.color + '22', color: tag.color, borderColor: tag.color + '33' } : undefined}
              >
                {tag.name}
              </Badge>
            ))}
            {assigneeName && (
              <span className="text-caption text-gray-400">{assigneeName}</span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="text-caption text-gray-400">{ticketNr}</span>
          {date && (
            <p className="text-caption text-gray-400 mt-0.5">
              {new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function groupTickets(tickets: TanssTicket[]): Record<string, TanssTicket[]> {
  const groups: Record<string, TanssTicket[]> = {}
  for (const t of tickets) {
    const group = t.queue || t.department?.name || t.categoryName || t.category || 'Sonstige'
    if (!groups[group]) groups[group] = []
    groups[group].push(t)
  }
  return groups
}

function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Guten Morgen'
  if (hour < 18) return 'Guten Nachmittag'
  return 'Guten Abend'
}

// ─── Bare Widget (fuer Widgets ohne Kopfzeile, z.B. Clock / Banner) ──────────
// Stellt im Bearbeiten-Modus Drag-Handle + Entfernen-Button bereit, sodass
// auch "nackte" Widgets verschoben und geloescht werden koennen.

function BareWidget({
  isEditing,
  onRemove,
  children,
}: {
  isEditing: boolean
  onRemove: () => void
  children: React.ReactNode
}) {
  return (
    <div className="relative h-full w-full flex items-center">
      {children}
      {isEditing && (
        <div className="absolute top-1 right-1 flex items-center gap-1 z-10">
          <span
            className="drag-handle cursor-grab p-1 bg-white/90 rounded shadow-sm"
            aria-label="Verschieben"
          >
            <GripVertical className="w-3.5 h-3.5 text-gray-500" />
          </span>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 bg-white/90 rounded shadow-sm text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors"
            aria-label="Widget entfernen"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
